import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Eye,
  Edit3,
  Trash2,
  RefreshCw,
  X,
  Clock,
  Lock,
  Church,
  Building2,
  Briefcase,
  Layers,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Send,
  ShieldCheck,
  Activity,
  Phone,
  Calendar,
  User
} from 'lucide-react';

// ============================================================
// ACCOUNT → TEAM — Wave 1 (UI only, no backend)
// Customer-managed team for Church Pro / Agency / Agency Elite plans.
// ============================================================

const ROLES = [
  { value: 'org_owner',     label: 'Organization Owner', accent: 'rose',   access: ['Everything'] },
  { value: 'org_admin',     label: 'Organization Admin', accent: 'amber',  access: ['Team', 'Settings', 'Reporting', 'Organization Management'] },
  { value: 'media_director',label: 'Media Director',     accent: 'pink',   access: ['Content Systems', 'Publishing', 'Creator Growth Engine'] },
  { value: 'content_manager',label: 'Content Manager',   accent: 'cyan',   access: ['Content Workflows', 'Scheduling', 'Publishing'] },
  { value: 'analyst',       label: 'Analyst',            accent: 'violet', access: ['Analytics', 'Weekly Digest', 'Reporting'] },
  { value: 'volunteer',     label: 'Volunteer',          accent: 'orange', access: ['Assigned Tasks', 'Limited Content Areas'] },
  { value: 'viewer',        label: 'Viewer',             accent: 'green',  access: ['Read Only'] }
];

const SCOPES = [
  { value: 'full_org',         label: 'Full Organization' },
  { value: 'selected_org',     label: 'Selected Organizations' },
  { value: 'assigned_tasks',   label: 'Assigned Tasks' },
  { value: 'reporting_only',   label: 'Reporting Only' },
  { value: 'view_only',        label: 'View Only' }
];

const MOCK_MEMBERS = [
  { id: 'tm1', name: 'Sarah Williams', email: 'sarah@firstcommunity.org',  role: 'org_owner',       scope: 'full_org',       status: 'active',  initials: 'SW', joined: 'Joined Jan 2026' },
  { id: 'tm2', name: 'Pastor John Reed',email: 'john@firstcommunity.org',  role: 'org_admin',       scope: 'full_org',       status: 'active',  initials: 'JR', joined: 'Joined Jan 2026' },
  { id: 'tm3', name: 'Jessica Green',   email: 'jessica@firstcommunity.org', role: 'analyst',        scope: 'reporting_only', status: 'pending', initials: 'JG', joined: 'Invited 2d ago' },
  { id: 'tm4', name: 'Elena Vasquez',   email: 'elena@hopefellowship.org', role: 'media_director',  scope: 'full_org',       status: 'active',  initials: 'EV', joined: 'Joined Dec 2025' },
  { id: 'tm5', name: 'Priya Patel',     email: 'priya@firstcommunity.org',  role: 'content_manager',scope: 'full_org',       status: 'active',  initials: 'PP', joined: 'Joined Nov 2025' },
  { id: 'tm6', name: 'Tomás Rivera',    email: 'tomas@hopefellowship.org',  role: 'content_manager',scope: 'selected_org',   status: 'active',  initials: 'TR', joined: 'Joined Nov 2025' },
  { id: 'tm7', name: 'Amara Okafor',    email: 'amara@gracechapel.org',     role: 'viewer',         scope: 'view_only',      status: 'active',  initials: 'AO', joined: 'Joined Oct 2025' },
  { id: 'tm8', name: 'Liam Chen',       email: 'liam@firstcommunity.org',   role: 'volunteer',      scope: 'assigned_tasks', status: 'active',  initials: 'LC', joined: 'Joined Oct 2025' }
];

const MOCK_PENDING = [
  { id: 'inv1', email: 'rachel@firstcommunity.org', role: 'analyst',         sent: 'Feb 12, 2026', expires: 'Feb 19, 2026' },
  { id: 'inv2', email: 'noah@hopefellowship.org',    role: 'volunteer',       sent: 'Feb 10, 2026', expires: 'Feb 17, 2026' },
  { id: 'inv3', email: 'morgan@gracechapel.org',     role: 'content_manager', sent: 'Feb 09, 2026', expires: 'Feb 16, 2026' }
];

// Church Pro uses Locations (campuses of a single church/org).
const LOCATIONS = [
  {
    id: 'loc1',
    name: 'First Community Church — Main Campus',
    region: 'Dallas, TX',
    seats: 5,
    seatsUsed: 4,
    members: ['Organization Owner', 'Organization Admin', 'Content Manager', 'Volunteer']
  },
  {
    id: 'loc2',
    name: 'First Community Church — North Campus',
    region: 'Plano, TX',
    seats: 4,
    seatsUsed: 3,
    members: ['Media Director', 'Content Manager', 'Analyst']
  },
  {
    id: 'loc3',
    name: 'First Community Church — Online Campus',
    region: 'Digital',
    seats: 3,
    seatsUsed: 2,
    members: ['Organization Admin', 'Viewer']
  }
];

// Agency / Agency Elite use Departments (functional teams across many clients).
const DEPARTMENTS = [
  { id: 'dep1', name: 'Content Department',     region: 'Production',        seats: 8,  seatsUsed: 6, members: ['Organization Admin', 'Media Director', 'Content Manager'] },
  { id: 'dep2', name: 'Editing Department',     region: 'Post-production',   seats: 6,  seatsUsed: 5, members: ['Media Director', 'Content Manager'] },
  { id: 'dep3', name: 'Client Success',         region: 'Account management',seats: 5,  seatsUsed: 4, members: ['Organization Admin', 'Analyst'] },
  { id: 'dep4', name: 'Community Management',   region: 'Audience',          seats: 4,  seatsUsed: 3, members: ['Content Manager', 'Analyst'] },
  { id: 'dep5', name: 'Analytics',              region: 'Reporting',         seats: 3,  seatsUsed: 2, members: ['Analyst', 'Viewer'] },
  { id: 'dep6', name: 'Sales',                  region: 'Growth',            seats: 4,  seatsUsed: 3, members: ['Organization Admin', 'Viewer'] }
];

// Read-only Church Partner Program assignments (contact profiles only)
const ASSIGNED_PARTNERS = [
  {
    id: 'cpl1',
    role: 'Church Partner Lead',
    name: 'Marcus Hill',
    initials: 'MH',
    accent: 'amber',
    certified: true,
    status: 'Active',
    email: 'marcus@consistency.app',
    phone: '(555) 212-4400',
    preferred: 'Schedule Meeting'
  },
  {
    id: 'cpr1',
    role: 'Church Partner Rep',
    name: 'David Kim',
    initials: 'DK',
    accent: 'orange',
    certified: true,
    status: 'Active',
    email: 'david@consistency.app',
    phone: '(555) 212-4401',
    preferred: 'Call or Text'
  }
];

// Plan -> entitlements (assignmentLabel + assignmentTitle drive section copy)
const PLAN_META = {
  locked:        { label: 'Starter / Creator / Pro', tone: 'locked',   limit: 0,         maxOrgs: 0,           assignmentLabel: 'Assigned',           assignmentTitle: 'Assignments',           assignmentSubtitle: '',                                                              dataset: 'locations' },
  church_pro:    { label: 'Church Pro',              tone: 'unlocked', limit: 10,        maxOrgs: 3,           assignmentLabel: 'Assigned Locations', assignmentTitle: 'Location Assignments',   assignmentSubtitle: 'Manage which team members support each church location.',     dataset: 'locations' },
  agency:        { label: 'Agency',                  tone: 'future',   limit: 25,        maxOrgs: 10,          assignmentLabel: 'Assigned Departments',assignmentTitle: 'Department Assignments', assignmentSubtitle: 'Manage which team members support each department.',          dataset: 'departments' },
  agency_elite:  { label: 'Agency Elite',            tone: 'future',   limit: 'Unlimited', maxOrgs: 'Unlimited', assignmentLabel: 'Assigned Departments',assignmentTitle: 'Department Assignments', assignmentSubtitle: 'Manage which team members support each department.',          dataset: 'departments' }
};

const PREVIEW_OPTIONS = [
  { value: 'locked',       label: 'Locked (Starter/Creator/Pro)' },
  { value: 'church_pro',   label: 'Church Pro' },
  { value: 'agency',       label: 'Agency (Future)' },
  { value: 'agency_elite', label: 'Agency Elite (Future)' }
];

const STATUS_TONE = {
  active:  { label: 'Active',  border: 'border-green-500/30', bg: 'bg-green-500/10', text: 'text-green-300', icon: CheckCircle2 },
  pending: { label: 'Pending', border: 'border-amber-500/30', bg: 'bg-amber-500/10', text: 'text-amber-300', icon: Clock }
};

// helpers
const roleMeta = (val) => ROLES.find((r) => r.value === val);
const scopeMeta = (val) => SCOPES.find((s) => s.value === val);

const SourceChip = ({ source }) => (
  <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#555E6E]">
    <span className="w-1 h-1 rounded-full bg-[#3A4150]" />
    {source}
  </span>
);

const SectionHeader = ({ icon: Icon, accent = 'cyan', title, subtitle, source, right }) => (
  <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
    <div>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 text-${accent}-400`} />
        <h2 className="text-base font-semibold text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-[11px] text-[#8A93A6] mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">
      {source && <SourceChip source={source} />}
      {right}
    </div>
  </div>
);

// ============================================================
// LOCKED STATE
// ============================================================
function LockedState({ isAdmin, previewMode, setPreviewMode, embedded = false }) {
  return (
    <div className={embedded ? '' : 'container mx-auto p-6 max-w-6xl'} data-testid="team-locked-state">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {!embedded && (
              <>
                <Users className="h-6 w-6 text-cyan-400" />
                <h1 className="text-3xl font-bold text-white">Team</h1>
              </>
            )}
            <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10 uppercase tracking-wider font-semibold">
              Team Management
            </Badge>
            <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider font-semibold gap-1">
              <Lock className="h-3 w-3" />
              Locked
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
                Admin Preview
              </Badge>
            )}
          </div>
          <p className="text-[#8A93A6] max-w-2xl">
            Invite team members, assign roles, and manage access for your organization.
          </p>
        </div>

        {isAdmin && (
          <Select value={previewMode} onValueChange={setPreviewMode}>
            <SelectTrigger className="h-9 w-[250px] bg-pink-500/[0.06] border-pink-500/25 text-pink-200 text-xs" data-testid="team-preview-select">
              <Sparkles className="h-3.5 w-3.5 mr-1 text-pink-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PREVIEW_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Locked CTA card */}
      <Card className="glass border-violet-500/20 ring-1 ring-violet-500/10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-violet-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
        <CardContent className="p-8 relative text-center">
          <div className="w-14 h-14 rounded-full bg-violet-500/15 border border-violet-500/30 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-6 w-6 text-violet-300" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Upgrade Required</h2>
          <p className="text-sm text-[#8A93A6] max-w-xl mx-auto mb-6 leading-relaxed">
            Team Management is available with the <span className="text-cyan-300 font-semibold">Church Pro</span>,{' '}
            <span className="text-cyan-300 font-semibold">Agency</span>, and{' '}
            <span className="text-cyan-300 font-semibold">Agency Elite</span> plans. Invite collaborators, assign roles, and manage access across your organizations.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link to="/app/account/billing">
              <Button variant="outline" data-testid="team-compare-plans-btn">
                Compare Plans
              </Button>
            </Link>
            <Link to="/app/account/billing">
              <Button className="bg-violet-500 hover:bg-violet-400 text-white" data-testid="team-upgrade-btn">
                <Sparkles className="h-4 w-4 mr-1.5" />
                Upgrade
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Plan tier preview */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { id: 'church_pro',  name: 'Church Pro',  status: 'Available',         seats: 'Up to 10 seats',     orgs: 'Up to 3 locations',    accent: 'rose', cta: 'Available' },
          { id: 'agency',      name: 'Agency',      status: 'Future',            seats: 'Up to 25 seats',     orgs: 'Up to 10 departments', accent: 'cyan',  cta: 'Coming Soon' },
          { id: 'agency_elite',name: 'Agency Elite',status: 'Future',            seats: 'Unlimited seats',    orgs: 'Unlimited departments',accent: 'violet', cta: 'Coming Soon' }
        ].map((p) => (
          <Card key={p.id} className="glass border-white/5" data-testid={`plan-preview-${p.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-white">{p.name}</p>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border-${p.accent}-500/30 text-${p.accent}-300 bg-${p.accent}-500/10`}>
                  {p.cta}
                </Badge>
              </div>
              <p className="text-[11px] text-[#8A93A6] mb-0.5">{p.seats}</p>
              <p className="text-[11px] text-[#8A93A6]">{p.orgs}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// UNLOCKED STATE
// ============================================================
function UnlockedState({ isAdmin, previewMode, setPreviewMode, planKey, embedded = false }) {
  const planMeta = PLAN_META[planKey];
  const planBadgeTone =
    planKey === 'church_pro' ? 'rose' :
    planKey === 'agency'     ? 'cyan' :
                                'violet';

  const [members] = useState(MOCK_MEMBERS);
  const [pending, setPending] = useState(MOCK_PENDING);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'analyst', scope: 'reporting_only' });

  // Plan-aware assignment dataset (Locations vs Departments).
  const assignments = planMeta.dataset === 'departments' ? DEPARTMENTS : LOCATIONS;

  const activeRoles = useMemo(() => new Set(members.filter((m) => m.status === 'active').map((m) => m.role)).size, [members]);

  const sendInvite = () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    toast.success(`Invite sent to ${inviteForm.email}`);
    setInviteForm({ name: '', email: '', role: 'analyst', scope: 'reporting_only' });
  };

  const cancelInvite = (id) => {
    setPending((p) => p.filter((x) => x.id !== id));
    toast.success('Invite cancelled');
  };

  const resendInvite = (email) => toast.info(`Invite re-sent to ${email}`);

  return (
    <div className={embedded ? '' : 'container mx-auto p-6 max-w-6xl'} data-testid="team-unlocked-state">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {!embedded && (
              <>
                <Users className="h-6 w-6 text-cyan-400" />
                <h1 className="text-3xl font-bold text-white">Team</h1>
              </>
            )}
            <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10 uppercase tracking-wider font-semibold">
              Team Management
            </Badge>
            <Badge variant="outline" className={`text-[10px] border-${planBadgeTone}-500/30 text-${planBadgeTone}-300 bg-${planBadgeTone}-500/10 uppercase tracking-wider font-semibold`}>
              {planMeta.label}
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
                Admin Preview
              </Badge>
            )}
          </div>
          <p className="text-[#8A93A6] max-w-2xl">
            Invite team members, assign roles, and manage access for your organization.
          </p>
        </div>

        {isAdmin && (
          <Select value={previewMode} onValueChange={setPreviewMode}>
            <SelectTrigger className="h-9 w-[250px] bg-pink-500/[0.06] border-pink-500/25 text-pink-200 text-xs" data-testid="team-preview-select">
              <Sparkles className="h-3.5 w-3.5 mr-1 text-pink-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PREVIEW_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* SECTION 1 — Team Overview */}
      <section className="mb-7" data-testid="section-overview">
        <SectionHeader icon={Activity} accent="cyan" title="Team Overview" subtitle="Snapshot of your organization's team." source="Account access" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { id: 'members', label: 'Team Members',           value: members.length, icon: Users,       accent: 'cyan' },
            { id: 'pending', label: 'Pending Invites',         value: pending.length, icon: Mail,        accent: 'amber' },
            { id: 'roles',   label: 'Active Roles',            value: activeRoles,    icon: Shield,      accent: 'violet' },
            { id: 'orgs',    label: planMeta.assignmentLabel,  value: assignments.length, icon: Building2, accent: 'rose' }
          ].map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.id} className="glass border-white/5 hover:border-white/10 transition-colors" data-testid={`overview-${s.id}`}>
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-7 h-7 rounded-md bg-${s.accent}-500/10 border border-${s.accent}-500/20 flex items-center justify-center`}>
                      <Icon className={`h-3.5 w-3.5 text-${s.accent}-400`} />
                    </div>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium mb-0.5">{s.label}</p>
                  <p className="text-2xl font-bold text-white tabular-nums leading-none">{s.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {/* Plan capacity */}
        <Card className="glass border-white/5 mt-3">
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-[11px] text-[#8A93A6]">
              <Layers className="h-3.5 w-3.5 text-cyan-400" />
              <span>Plan capacity</span>
              <span className="text-white font-semibold">{planMeta.limit}</span>
              <span>seats</span>
              <span className="text-[#3A4150]">•</span>
              <span className="text-white font-semibold">{planMeta.maxOrgs}</span>
              <span>{planMeta.dataset === 'departments' ? 'departments' : 'locations'}</span>
            </div>
            <Link to="/app/account/billing" className="text-[10px] uppercase tracking-wider text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
              Manage Plan
              <ArrowRight className="h-3 w-3" />
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 2 — Invite Team Member */}
      <section className="mb-7" data-testid="section-invite">
        <SectionHeader icon={UserPlus} accent="pink" title="Invite Team Member" subtitle="Send a role-scoped invite to a teammate." source="Invitations" />
        <Card className="glass border-white/5">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <div className="md:col-span-1">
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Name</p>
                <Input
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Full name"
                  className="h-9 bg-[#1C1F26] border-white/10 text-white text-xs"
                  data-testid="invite-name-input"
                />
              </div>
              <div className="md:col-span-1">
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Email</p>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((s) => ({ ...s, email: e.target.value }))}
                  placeholder="name@organization.org"
                  className="h-9 bg-[#1C1F26] border-white/10 text-white text-xs"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="md:col-span-1">
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Role</p>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm((s) => ({ ...s, role: v }))}>
                  <SelectTrigger className="h-9 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1">
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] mb-1.5 font-medium">Access Scope</p>
                <Select value={inviteForm.scope} onValueChange={(v) => setInviteForm((s) => ({ ...s, scope: v }))}>
                  <SelectTrigger className="h-9 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid="invite-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPES.map((sc) => (
                      <SelectItem key={sc.value} value={sc.value}>{sc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-[10px] text-[#8A93A6]">
                Invitees receive an email with a secure activation link valid for 7 days.
              </p>
              <Button
                onClick={sendInvite}
                className="bg-pink-500 hover:bg-pink-400 text-white"
                data-testid="send-invite-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Send Invite
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 3 — Team Members */}
      <section className="mb-7" data-testid="section-members">
        <SectionHeader
          icon={Users}
          accent="cyan"
          title="Team Members"
          subtitle="Everyone with access to your organization."
          source="Roster"
          right={
            <Badge variant="outline" className="text-[10px] border-white/10 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider">
              {members.length} of {planMeta.limit} seats
            </Badge>
          }
        />
        <Card className="glass border-white/5">
          <CardContent className="p-0">
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-white/[0.05] text-[10px] uppercase tracking-[0.14em] text-[#555E6E] font-semibold">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">Scope</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            <div className="divide-y divide-white/[0.04]">
              {members.map((m) => {
                const role = roleMeta(m.role);
                const scope = scopeMeta(m.scope);
                const st = STATUS_TONE[m.status];
                const StatusIcon = st.icon;
                return (
                  <div key={m.id} className="grid grid-cols-2 md:grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-white/[0.02] transition-colors" data-testid={`member-${m.id}`}>
                    <div className="md:col-span-3 flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-full bg-${role?.accent || 'cyan'}-500/15 border border-${role?.accent || 'cyan'}-500/30 flex items-center justify-center shrink-0`}>
                        <span className={`text-[11px] font-bold text-${role?.accent || 'cyan'}-200`}>{m.initials}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-white truncate">{m.name}</p>
                        <p className="text-[10px] text-[#8A93A6] truncate">{m.joined}</p>
                      </div>
                    </div>
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-[11px] text-[#C9CFDA] truncate">{m.email}</p>
                    </div>
                    <div className="md:col-span-2">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border-${role?.accent}-500/30 text-${role?.accent}-300 bg-${role?.accent}-500/10`}>
                        {role?.label}
                      </Badge>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-[11px] text-[#8A93A6]">{scope?.label}</p>
                    </div>
                    <div className="md:col-span-1">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${st.border} ${st.bg} ${st.text} gap-1`}>
                        <StatusIcon className="h-2.5 w-2.5" />
                        {st.label}
                      </Badge>
                    </div>
                    <div className="md:col-span-1 flex justify-end items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toast.info(`Edit ${m.name}`)}
                        className="p-1 rounded-md text-[#8A93A6] hover:text-white hover:bg-white/[0.05]"
                        title="Edit"
                        data-testid={`edit-${m.id}`}
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => toast.info(`Reassign ${m.name}`)}
                        className="p-1 rounded-md text-[#8A93A6] hover:text-white hover:bg-white/[0.05]"
                        title="Reassign"
                        data-testid={`reassign-${m.id}`}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => toast.info(`Remove ${m.name}`)}
                        className="p-1 rounded-md text-[#8A93A6] hover:text-red-300 hover:bg-red-500/10"
                        title="Remove"
                        data-testid={`remove-${m.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 4 — Pending Invites */}
      <section className="mb-7" data-testid="section-pending">
        <SectionHeader icon={Mail} accent="amber" title="Pending Invites" subtitle="Awaiting activation by the recipient." source="Invitations" />
        {pending.length === 0 ? (
          <Card className="glass border-white/5">
            <CardContent className="p-6 text-center">
              <p className="text-xs text-[#8A93A6]">No pending invites. Use the form above to add a teammate.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pending.map((p) => {
              const role = roleMeta(p.role);
              return (
                <Card key={p.id} className="glass border-amber-500/15 bg-amber-500/[0.03]" data-testid={`pending-${p.id}`}>
                  <CardContent className="p-3.5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-9 h-9 rounded-md bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                        <Mail className="h-4 w-4 text-amber-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-white truncate">{p.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border-${role?.accent}-500/30 text-${role?.accent}-300 bg-${role?.accent}-500/10`}>
                            {role?.label}
                          </Badge>
                          <span className="text-[10px] text-[#8A93A6]">Sent {p.sent}</span>
                        </div>
                        <p className="text-[10px] text-[#8A93A6] mt-0.5">Expires {p.expires}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px]"
                        onClick={() => resendInvite(p.email)}
                        data-testid={`resend-${p.id}`}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Resend
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] hover:text-red-300 hover:border-red-500/40"
                        onClick={() => cancelInvite(p.id)}
                        data-testid={`cancel-${p.id}`}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* SECTION 5 — Role Preview */}
      <section className="mb-7" data-testid="section-roles">
        <SectionHeader icon={Shield} accent="violet" title="Role Preview" subtitle="What each role can access inside your organization." source="Permissions" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {ROLES.map((r) => (
            <Card key={r.value} className="glass border-white/5 hover:border-white/10 transition-colors" data-testid={`role-${r.value}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-md bg-${r.accent}-500/10 border border-${r.accent}-500/20 flex items-center justify-center`}>
                      <Shield className={`h-3.5 w-3.5 text-${r.accent}-400`} />
                    </div>
                    <p className="text-sm font-semibold text-white">{r.label}</p>
                  </div>
                </div>
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium mb-2">Can Access</p>
                <ul className="space-y-1">
                  {r.access.map((a) => (
                    <li key={a} className="flex items-center gap-2 text-[11px] text-[#C9CFDA]">
                      <ChevronRight className={`h-3 w-3 text-${r.accent}-400`} />
                      {a}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* SECTION 6 — Assignments (Locations or Departments based on plan) */}
      <section className="mb-7" data-testid="section-orgs">
        <SectionHeader
          icon={Building2}
          accent="rose"
          title={planMeta.assignmentTitle}
          subtitle={planMeta.assignmentSubtitle}
          source={planMeta.dataset === 'departments' ? 'Departments' : 'Locations'}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {assignments.map((o) => {
            const NodeIcon = planMeta.dataset === 'departments' ? Briefcase : Church;
            return (
              <Card key={o.id} className="glass border-white/5 hover:border-white/10 transition-colors" data-testid={`org-${o.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-md bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                      <NodeIcon className="h-4 w-4 text-rose-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-white truncate">{o.name}</p>
                      <p className="text-[10px] text-[#8A93A6] truncate">{o.region}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-white/10 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider shrink-0">
                      {o.seatsUsed}/{o.seats}
                    </Badge>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium mb-2">Assigned Team</p>
                  <div className="flex flex-wrap gap-1.5">
                    {o.members.map((m) => (
                      <Badge key={m} variant="outline" className="text-[10px] uppercase tracking-wider border-white/10 text-[#C9CFDA] bg-white/[0.03]">
                        {m}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => toast.info(`Opening ${o.name}`)}
                      className="text-[10px] uppercase tracking-wider text-rose-300 hover:text-rose-200 inline-flex items-center gap-1"
                    >
                      Manage
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* SECTION 7 — Assigned Support Team (informational contact profiles) */}
      <section className="mb-7" data-testid="section-assigned-partners">
        <SectionHeader
          icon={Users}
          accent="amber"
          title="Assigned Support Team"
          subtitle="Your assigned Church Partner contacts for support and onboarding."
          source="Church Partner Program"
          right={
            <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider gap-1">
              <Lock className="h-3 w-3" />
              Read Only
            </Badge>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ASSIGNED_PARTNERS.map((p) => (
            <Card key={p.id} className="glass border-amber-500/15 bg-amber-500/[0.02]" data-testid={`assigned-partner-${p.id}`}>
              <CardContent className="p-4">
                {/* Identity */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-full bg-${p.accent}-500/15 border border-${p.accent}-500/30 flex items-center justify-center shrink-0`}>
                    <span className={`text-[13px] font-bold text-${p.accent}-200`}>{p.initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium">{p.role}</p>
                    <p className="text-[14px] font-semibold text-white truncate">{p.name}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-green-500/30 text-green-300 bg-green-500/10 gap-1">
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        {p.status}
                      </Badge>
                      {p.certified && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-green-500/30 text-green-300 bg-green-500/10 gap-1">
                          <ShieldCheck className="h-2.5 w-2.5" />
                          Certified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact details */}
                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                    <Mail className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
                    <p className="text-[12px] text-white truncate">{p.email}</p>
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                    <Phone className="h-3.5 w-3.5 text-green-400 shrink-0" />
                    <p className="text-[12px] text-white">{p.phone}</p>
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-amber-500/15 bg-amber-500/[0.04] px-3 py-2">
                    <Sparkles className="h-3.5 w-3.5 text-amber-300 shrink-0" />
                    <p className="text-[11px] text-[#C9CFDA]">
                      <span className="text-[#8A93A6] uppercase tracking-wider text-[10px] mr-1">Preferred:</span>
                      {p.preferred}
                    </p>
                  </div>
                </div>

                {/* Actions (UI only) */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] flex-1"
                    onClick={() => toast.info(`Opening ${p.name}'s profile`)}
                    data-testid={`view-profile-${p.id}`}
                  >
                    <User className="h-3 w-3 mr-1" />
                    View Profile
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] flex-1"
                    onClick={() => toast.info(`Composing email to ${p.email}`)}
                    data-testid={`email-${p.id}`}
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] flex-1"
                    onClick={() => toast.info(`Calling ${p.phone}`)}
                    data-testid={`call-${p.id}`}
                  >
                    <Phone className="h-3 w-3 mr-1" />
                    Call
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-[10px] flex-1 bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25"
                    onClick={() => toast.info(`Opening scheduler with ${p.name}`)}
                    data-testid={`schedule-${p.id}`}
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    Schedule
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-[11px] text-[#8A93A6] leading-relaxed mt-3 px-1 flex items-start gap-1.5">
          <Lock className="h-3 w-3 mt-0.5 opacity-60 shrink-0" />
          Assigned support contacts are managed through the Church Partner Program. They cannot be edited, assigned, or removed from Team Management.
        </p>
      </section>

      {/* SECTION 8 — Security Information */}
      <section data-testid="section-security">
        <Card className="glass border-white/5">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-4 w-4 text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Role-Based Access</p>
              <p className="text-[11px] text-[#8A93A6] leading-relaxed mt-0.5">
                Team access is controlled by role assignments, plan type, and location or department permissions. Customer team members are managed here. Consistency staff and Church Partner contacts are managed separately through Admin and Church Partner systems.
              </p>
            </div>
            <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-300 bg-green-500/10 uppercase tracking-wider shrink-0 hidden sm:inline-flex">
              Enforced
            </Badge>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

// ============================================================
// EMPTY STATE (zero members + zero invites — Wave 1 placeholder)
// Currently shown when admin selects Church Pro/Agency/Agency Elite
// but data set is empty. For Wave 1 we keep MOCK_MEMBERS populated,
// so this is exported for future use & not rendered by default.
// ============================================================
// (Empty-state UX is handled inline by the unlocked state when both
//  members and pending arrays are empty; we keep the spec here for ref.)

// ============================================================
// MAIN
// ============================================================
export default function TeamMembers({ embedded = false } = {}) {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;

  // Map real plan_tier to lock state. For Wave 1, admins default to a
  // populated Church Pro preview so they can see the full UI.
  const realPlanKey = useMemo(() => {
    const tier = user?.plan_tier;
    if (tier === 'church_pro')   return 'church_pro';
    if (tier === 'agency')       return 'agency';
    if (tier === 'agency_elite') return 'agency_elite';
    return 'locked';
  }, [user]);

  const [previewMode, setPreviewMode] = useState(isAdmin ? 'church_pro' : realPlanKey);
  const planKey = isAdmin ? previewMode : realPlanKey;

  if (planKey === 'locked') {
    return <LockedState isAdmin={isAdmin} previewMode={previewMode} setPreviewMode={setPreviewMode} embedded={embedded} />;
  }
  return <UnlockedState isAdmin={isAdmin} previewMode={previewMode} setPreviewMode={setPreviewMode} planKey={planKey} embedded={embedded} />;
}
